import { memo, useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react"
import { useRouteLoaderData } from "react-router"
import { CheckIcon, ExternalLinkIcon, LoaderCircleIcon, PlayIcon, SearchIcon, XIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { config } from "@/utils/config"
import { getStorageKey, isTermValid, loadStoredConfig } from "@/utils/summarizer-config"

import { SummarizerConfiguration } from "./summarizer-configuration"
import {
  createMatchSnapshot,
  createViewingSession,
  getCaseFiles,
  getDocumentSearchResults,
  getPageImageUrl,
  loadViewerRuntime,
  loadPageImage,
  searchCases,
  submitDocumentSearch,
  type CaseResult,
  type CaseSearchRequest,
  type TestFile,
  type TestFileAttempt,
  type TestFileRun,
  type TestFileStatus,
  type TestMatch,
  type ViewerSearchColumn,
  type ViewerApiVersion,
} from "./summarizer-test-api"
import { createViewerSearchPatterns, getSkipReason, shouldAllowSearch } from "./summarizer-test-utils"

const PAGE_SIZE = 10
const FILE_CONCURRENCY = 10
const FILE_FILTERS = ["All", "Failed", "Skipped", "Success", "Flaky"] as const
const SELECTED_CONTROL_CLASS =
  "aria-pressed:bg-slate-900 aria-pressed:text-white aria-pressed:hover:bg-slate-800 dark:aria-pressed:bg-slate-200 dark:aria-pressed:text-slate-950 dark:aria-pressed:hover:bg-slate-300"

type FileFilter = (typeof FILE_FILTERS)[number]
interface RuntimeState {
  lensCode: string
  apiVersion: ViewerApiVersion
  caseSearch: ViewerSearchColumn[]
}

function fileKey(file: Pick<TestFile, "caseReference" | "fileReference">) {
  return `${encodeURIComponent(file.caseReference)}::${encodeURIComponent(file.fileReference)}`
}

function getFileRun(file: TestFile, labelName: string): TestFileRun {
  return file.runs?.[labelName] ?? {}
}

function getOutcome(attempt: TestFileAttempt) {
  if (attempt.status === "Excluded" || attempt.status === "Skipped") return "Skipped"
  if (attempt.status === "Cancelled" || attempt.status === "Failed") return "Failed"
  if (attempt.status === "Success") return "Success"
  return undefined
}

function isFlaky(run: TestFileRun) {
  let outcome = getOutcome(run)
  for (const attempt of run.previousAttempts ?? []) {
    const previousOutcome = getOutcome(attempt)
    if (previousOutcome == null) continue
    if (outcome != null && outcome !== previousOutcome) return true
    outcome = previousOutcome
  }
  return false
}

function isFieldFilter(column: ViewerSearchColumn) {
  return column.filterable === true && ["text", "autocomplete"].includes(column.filterConfig?.type ?? "")
}

function inProgress(status: TestFileStatus | undefined) {
  return (
    status === "Loading session" ||
    status === "Searching" ||
    status === "Waiting for results" ||
    status === "Rendering matches"
  )
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "An unexpected error occurred"
}

function buildCaseSearchRequest(
  columns: ViewerSearchColumn[],
  lensCode: string,
  searchText: string,
  searchMode: "all" | "any",
  searchFields: string[],
  filters: Record<string, string>,
  casesWithDocumentsOnly: boolean,
): CaseSearchRequest {
  const text = searchText.trim()
  const filter = columns
    .filter((column) => isFieldFilter(column) && (filters[column.fieldPath]?.trim() ?? "") !== "")
    .map((column) => {
      const value = filters[column.fieldPath].trim().replaceAll("'", "''")
      if (column.filterConfig?.type === "autocomplete") {
        if (column.isCollection !== false) {
          const [, main, sub = ""] = /^(.+?\/[^/]+)(.+)?/.exec(column.fieldPath) ?? ["", column.fieldPath, ""]
          return `${main}/any(it: it${sub} eq '${value}')`
        }
        return `${column.fieldPath} eq '${value}'`
      }
      return `search.ismatch('${value}', '${column.fieldPath}', 'simple', 'all')`
    })

  if (casesWithDocumentsOnly) filter.push("metadata/hasDocuments eq true")

  return {
    searchText: text === "" ? undefined : text,
    searchMode: text === "" ? undefined : searchMode,
    searchFields: text === "" ? undefined : searchFields,
    filter: filter.length === 0 ? undefined : filter.join(" and "),
    lensCode,
    retentionFilter: "All",
    expiryDate: null,
    orderBy: ["search.score() desc", "caseReference asc"],
    select: ["id", "caseReference", ...columns.map((column) => column.fieldPath)],
  }
}

function waitForResults(delay: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"))
      return
    }

    const timeout = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort)
      resolve()
    }, delay)
    const onAbort = () => {
      window.clearTimeout(timeout)
      reject(new DOMException("Aborted", "AbortError"))
    }
    signal.addEventListener("abort", onAbort, { once: true })
  })
}

export function SummarizerTest() {
  const data = useRouteLoaderData<typeof SummarizerConfiguration.loader>(SummarizerConfiguration.routeId)

  if (data == null || data.loadedProject === "") {
    return <p className="py-12 text-center text-muted-foreground">No brand is selected.</p>
  }

  const storageKey = getStorageKey(data.loadedOrganisation, data.loadedProject)

  return (
    <SummarizerTestRuntime
      key={storageKey}
      client={data.loadedOrganisation}
      brand={data.loadedProject}
      storageKey={storageKey}
    />
  )
}

interface SummarizerTestProps {
  client: string
  brand: string
  storageKey: string
}

function SummarizerTestRuntime({ client, brand, storageKey }: SummarizerTestProps) {
  const [runtime, setRuntime] = useState<RuntimeState | null>(null)
  const [runtimeError, setRuntimeError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()

    loadViewerRuntime(client, brand, controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) setRuntime(loaded)
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) setRuntimeError(errorMessage(error))
      })

    return () => {
      controller.abort()
    }
  }, [brand, client])

  return (
    <SummarizerTestRunner
      key={JSON.stringify(runtime)}
      client={client}
      brand={brand}
      storageKey={storageKey}
      runtime={runtime}
      runtimeError={runtimeError}
    />
  )
}

function SummarizerTestRunner({
  client,
  brand,
  storageKey,
  runtime,
  runtimeError,
}: SummarizerTestProps & { runtime: RuntimeState | null; runtimeError: string | null }) {
  const [configData] = useState(() => loadStoredConfig(storageKey))
  const labels = Object.values(configData)
  const [selectedLabelName, setSelectedLabelName] = useState(() => Object.keys(configData)[0] ?? "")
  const selectedLabel = configData[selectedLabelName]
  const runPrerequisite =
    selectedLabel == null
      ? "Create a configuration with at least one search term to run a test."
      : selectedLabel.rawSearchTerms.length === 0
        ? "Add at least one search term to run this configuration."
        : selectedLabel.rawSearchTerms.some((term) => !isTermValid(term))
          ? "Fix empty search terms or invalid regular expressions before running a test."
          : null
  const [stage, setStage] = useState<"select" | "results">("select")

  const [caseQuery, setCaseQuery] = useState("")
  const [searchMode, setSearchMode] = useState<"all" | "any">("all")
  const [searchFields, setSearchFields] = useState<string[]>(
    () => runtime?.caseSearch.filter((column) => column.searchable === true).map((column) => column.fieldPath) ?? [],
  )
  const [caseFieldFilters, setCaseFieldFilters] = useState<Record<string, string>>({})
  const [casesWithDocumentsOnly, setCasesWithDocumentsOnly] = useState(true)
  const [hasSearched, setHasSearched] = useState(false)
  const [caseResults, setCaseResults] = useState<CaseResult[]>([])
  const [casePage, setCasePage] = useState(1)
  const [caseTotalCount, setCaseTotalCount] = useState(0)
  const [caseSearchLoading, setCaseSearchLoading] = useState(false)
  const [caseSearchError, setCaseSearchError] = useState<string | null>(null)
  const [selectedCases, setSelectedCases] = useState<string[]>([])
  const selectedCasesRef = useRef(new Set<string>())
  const [filesByCase, setFilesByCase] = useState<Record<string, TestFile[]>>({})
  const [caseFileErrors, setCaseFileErrors] = useState<Record<string, string>>({})
  const [loadingCases, setLoadingCases] = useState<string[]>([])
  const [selectedFileKeys, setSelectedFileKeys] = useState<string[]>([])
  const [fileFilter, setFileFilter] = useState<FileFilter>("All")
  const [activeResultFileKey, setActiveResultFileKey] = useState<string | null>(null)
  const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null)
  const [checkedFileKeys, setCheckedFileKeys] = useState<string[]>([])
  const [resultClassificationFilters, setResultClassificationFilters] = useState<string[]>([])
  const [expandedResults, setExpandedResults] = useState<string[]>([])
  const [isRunning, setIsRunning] = useState(false)
  const [completedLabels, setCompletedLabels] = useState<string[]>([])
  const [runError, setRunError] = useState<string | null>(null)
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null)
  const runController = useRef<AbortController | null>(null)
  const caseSearchController = useRef<AbortController | null>(null)
  const activeCaseSearch = useRef<CaseSearchRequest | null>(null)
  const caseFileControllers = useRef(new Map<string, AbortController>())
  const previewUrlsByCase = useRef(new Map<string, Set<string>>())
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    const controllers = caseFileControllers.current
    const previewUrls = previewUrlsByCase.current

    return () => {
      isMounted.current = false
      caseSearchController.current?.abort()
      controllers.forEach((controller) => controller.abort())
      runController.current?.abort()
      previewUrls.forEach((urls) => urls.forEach((url) => URL.revokeObjectURL(url)))
      previewUrls.clear()
    }
  }, [])

  const allFiles = useMemo(() => Object.values(filesByCase).flat(), [filesByCase])
  const allSelectedCasesLoaded = selectedCases.every((caseReference) => Object.hasOwn(filesByCase, caseReference))
  const configuredSearchableColumns = runtime?.caseSearch.filter((column) => column.searchable === true) ?? []
  const searchableColumns =
    configuredSearchableColumns.length > 0
      ? configuredSearchableColumns
      : [{ fieldPath: "caseReference", displayName: "Case Reference", searchable: true }]
  const filterableTextColumns = runtime?.caseSearch.filter(isFieldFilter) ?? []
  const selectedFileKeySet = useMemo(() => new Set(selectedFileKeys), [selectedFileKeys])
  const selectedFiles = useMemo(
    () => allFiles.filter((file) => selectedFileKeySet.has(fileKey(file))),
    [allFiles, selectedFileKeySet],
  )
  const classifications = useMemo(
    () =>
      [
        ...new Set(
          selectedFiles.map((file) =>
            file.aiClassificationGroup == null || file.aiClassificationGroup === ""
              ? "Unknown"
              : file.aiClassificationGroup,
          ),
        ),
      ].sort(),
    [selectedFiles],
  )
  const classifiedFiles = useMemo(
    () =>
      selectedFiles.filter(
        (file) =>
          resultClassificationFilters.length === 0 ||
          resultClassificationFilters.includes(
            file.aiClassificationGroup == null || file.aiClassificationGroup === ""
              ? "Unknown"
              : file.aiClassificationGroup,
          ),
      ),
    [resultClassificationFilters, selectedFiles],
  )
  const filesByOutcome = useMemo(() => {
    const groups: Record<FileFilter, TestFile[]> = {
      All: classifiedFiles,
      Failed: [],
      Skipped: [],
      Success: [],
      Flaky: [],
    }
    for (const file of classifiedFiles) {
      const run = getFileRun(file, selectedLabelName)
      const outcome = getOutcome(run)
      if (outcome != null) groups[outcome].push(file)
      if (isFlaky(run)) groups.Flaky.push(file)
    }
    return groups
  }, [classifiedFiles, selectedLabelName])
  const visibleFiles = stage === "select" ? classifiedFiles : filesByOutcome[fileFilter]
  const activeFile = visibleFiles.find((file) => fileKey(file) === activeResultFileKey)
  const activeRun = activeFile == null ? undefined : getFileRun(activeFile, selectedLabelName)
  const attempts = activeRun == null ? [] : [...(activeRun.previousAttempts ?? []), activeRun]
  const activeAttempt = selectedAttempt == null ? activeRun : attempts[selectedAttempt]
  const visibleResults = useMemo(
    () => (activeFile == null ? [] : (activeAttempt?.results ?? []).map((result) => ({ file: activeFile, result }))),
    [activeFile, activeAttempt?.results],
  )
  const removedFiles = allFiles.filter((file) => !selectedFileKeySet.has(fileKey(file)))
  const checkedFileKeySet = useMemo(() => new Set(checkedFileKeys), [checkedFileKeys])
  const checkedFiles = selectedFiles.filter(
    (file) => checkedFileKeySet.has(fileKey(file)) && getOutcome(getFileRun(file, selectedLabelName)) != null,
  )
  const checkableFiles = visibleFiles.filter((file) => getOutcome(getFileRun(file, selectedLabelName)) != null)
  const allVisibleChecked =
    checkableFiles.length > 0 && checkableFiles.every((file) => checkedFileKeySet.has(fileKey(file)))
  const isRunComplete = completedLabels.includes(selectedLabelName) && !isRunning
  const totalPages = Math.max(1, Math.ceil(caseTotalCount / PAGE_SIZE))
  const activeFieldFilterCount = filterableTextColumns.filter(
    (column) => (caseFieldFilters[column.fieldPath]?.trim() ?? "") !== "",
  ).length

  function updateFile(target: TestFile, updater: (current: TestFile) => TestFile) {
    if (!isMounted.current) return
    const key = fileKey(target)
    setFilesByCase((current) => ({
      ...current,
      [target.caseReference]: (current[target.caseReference] ?? []).map((file) =>
        fileKey(file) === key ? updater(file) : file,
      ),
    }))
  }

  function updateFileRun(target: TestFile, labelName: string, updater: (current: TestFileRun) => TestFileRun) {
    updateFile(target, (current) => ({
      ...current,
      runs: {
        ...current.runs,
        [labelName]: updater(getFileRun(current, labelName)),
      },
    }))
  }

  const searchCasePage = useCallback(
    async (page: number, request = activeCaseSearch.current) => {
      if (runController.current != null || runtime == null) return
      if (request == null) return

      setHasSearched(true)
      caseSearchController.current?.abort()
      const controller = new AbortController()
      caseSearchController.current = controller
      setCaseSearchLoading(true)
      setCaseSearchError(null)
      setCasePage(page)

      try {
        const response = await searchCases(client, brand, request, page, PAGE_SIZE, controller.signal)
        if (!controller.signal.aborted) {
          setCaseResults(response.items)
          setCaseTotalCount(response.totalCount)
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setCaseSearchError(errorMessage(error))
          setCaseResults([])
        }
      } finally {
        if (!controller.signal.aborted) setCaseSearchLoading(false)
      }
    },
    [brand, client, runtime],
  )

  useEffect(() => {
    if (runtime == null) return

    const fields = runtime.caseSearch.filter((column) => column.searchable === true).map((column) => column.fieldPath)
    const request = buildCaseSearchRequest(runtime.caseSearch, runtime.lensCode, "", "all", fields, {}, true)
    activeCaseSearch.current = request
    void searchCasePage(1, request)
  }, [runtime, searchCasePage])

  function submitCaseSearch(documentsOnly = casesWithDocumentsOnly) {
    if (runtime == null) return
    const request = buildCaseSearchRequest(
      runtime.caseSearch ?? [],
      runtime.lensCode,
      caseQuery,
      searchMode,
      searchFields,
      caseFieldFilters,
      documentsOnly,
    )
    activeCaseSearch.current = request
    void searchCasePage(1, request)
  }

  function handleCaseSearch(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    submitCaseSearch()
  }

  function resetCaseSearch() {
    if (runtime == null) return
    setCaseQuery("")
    setSearchMode("all")
    setSearchFields(searchableColumns.map((column) => column.fieldPath))
    setCaseFieldFilters({})
    setCasesWithDocumentsOnly(true)
    const request = buildCaseSearchRequest(runtime.caseSearch ?? [], runtime.lensCode, "", "all", [], {}, true)
    activeCaseSearch.current = request
    void searchCasePage(1, request)
  }

  function toggleSearchField(fieldPath: string) {
    setSearchFields((current) =>
      current.includes(fieldPath) ? current.filter((field) => field !== fieldPath) : [...current, fieldPath],
    )
  }

  async function toggleCase(caseReference: string) {
    if (isRunning) {
      setSelectionNotice("Case and file selection is locked while a test run is in progress.")
      return
    }

    setSelectionNotice(null)
    if (selectedCasesRef.current.has(caseReference)) {
      previewUrlsByCase.current.get(caseReference)?.forEach((url) => URL.revokeObjectURL(url))
      previewUrlsByCase.current.delete(caseReference)
      selectedCasesRef.current.delete(caseReference)
      caseFileControllers.current.get(caseReference)?.abort()
      caseFileControllers.current.delete(caseReference)
      setSelectedCases([...selectedCasesRef.current])
      setFilesByCase((current) => {
        const next = { ...current }
        const removedFiles = next[caseReference] ?? []
        for (const file of removedFiles) caseFileControllers.current.delete(fileKey(file))
        delete next[caseReference]
        return next
      })
      const casePrefix = `${encodeURIComponent(caseReference)}::`
      setSelectedFileKeys((current) => current.filter((key) => !key.startsWith(casePrefix)))
      setCheckedFileKeys((current) => current.filter((key) => !key.startsWith(casePrefix)))
      return
    }

    selectedCasesRef.current.add(caseReference)
    setSelectedCases([...selectedCasesRef.current])
    const controller = new AbortController()
    caseFileControllers.current.set(caseReference, controller)
    setLoadingCases((current) => [...current, caseReference])
    setCaseFileErrors((current) => {
      const next = { ...current }
      delete next[caseReference]
      return next
    })

    try {
      const files = await getCaseFiles(client, brand, caseReference, controller.signal)
      if (!controller.signal.aborted && selectedCasesRef.current.has(caseReference)) {
        setFilesByCase((current) => ({ ...current, [caseReference]: files }))
        setSelectedFileKeys((current) => [...new Set([...current, ...files.map(fileKey)])])
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        setCaseFileErrors((current) => ({ ...current, [caseReference]: errorMessage(error) }))
      }
    } finally {
      if (caseFileControllers.current.get(caseReference) === controller) {
        caseFileControllers.current.delete(caseReference)
      }
      if (isMounted.current) setLoadingCases((current) => current.filter((item) => item !== caseReference))
    }
  }

  function toggleCaseFiles(caseReference: string) {
    if (isRunning) {
      setSelectionNotice("Case and file selection is locked while a test run is in progress.")
      return
    }

    const caseFileKeys = new Set((filesByCase[caseReference] ?? []).map(fileKey))
    setSelectionNotice(null)
    setSelectedFileKeys((current) => {
      const hasSelectedFiles = current.some((key) => caseFileKeys.has(key))
      return hasSelectedFiles
        ? current.filter((key) => !caseFileKeys.has(key))
        : [...new Set([...current, ...caseFileKeys])]
    })
  }

  function selectResultFile(key: string) {
    setActiveResultFileKey(key)
    setSelectedAttempt(null)
    setExpandedResults([])
  }

  function clearTestResults() {
    previewUrlsByCase.current.forEach((urls) => urls.forEach((url) => URL.revokeObjectURL(url)))
    previewUrlsByCase.current.clear()
    setFilesByCase((current) =>
      Object.fromEntries(
        Object.entries(current).map(([key, files]) => [key, files.map((file) => ({ ...file, runs: {} }))]),
      ),
    )
    setCompletedLabels([])
    setFileFilter("All")
    setResultClassificationFilters([])
    setCheckedFileKeys([])
    selectResultFile("")
    setRunError(null)
  }

  function changeConfiguration(labelName: string) {
    if (runController.current != null) return
    setSelectedLabelName(labelName)
    clearTestResults()
    setStage("select")
  }

  function removeFile(file: TestFile) {
    if (isRunning) {
      setSelectionNotice("Case and file selection is locked while a test run is in progress.")
      return
    }

    const key = fileKey(file)
    setSelectionNotice(null)
    setSelectedFileKeys((current) => current.filter((item) => item !== key))
    setCheckedFileKeys((current) => current.filter((item) => item !== key))
  }

  async function runFile(
    file: TestFile,
    patterns: Array<{ pattern: string }>,
    controller: AbortController,
    apiVersion: ViewerApiVersion,
    labelName: string,
  ) {
    const signal = controller.signal
    const createdUrls: string[] = []
    const searchController = new AbortController()
    const cancelSearch = () => searchController.abort()
    let searchTimeout: ReturnType<typeof setTimeout> | undefined
    let searchTimedOut = false

    try {
      let sessionId = file.sessionId
      if (sessionId == null) {
        updateFileRun(file, labelName, (current) => ({
          ...current,
          status: "Loading session",
          results: [],
          error: undefined,
        }))
        sessionId = await createViewingSession(client, brand, file, signal)
        updateFile(file, (current) => ({ ...current, sessionId }))
      }
      const activeSessionId = sessionId

      if (signal.aborted) throw new DOMException("Aborted", "AbortError")
      signal.addEventListener("abort", cancelSearch, { once: true })
      searchTimeout = setTimeout(() => {
        searchTimedOut = true
        searchController.abort()
      }, 60_000)
      const searchSignal = searchController.signal
      updateFileRun(file, labelName, (current) => ({ ...current, status: "Searching" }))
      const processId = await submitDocumentSearch(client, activeSessionId, apiVersion, patterns, searchSignal)

      updateFileRun(file, labelName, (current) => ({ ...current, status: "Waiting for results" }))
      let page = await getDocumentSearchResults(client, processId, apiVersion, undefined, searchSignal)
      const searchResults = [...page.results]

      while (page.continueToken != null) {
        if (searchSignal.aborted) throw new DOMException("Aborted", "AbortError")
        await waitForResults(page.continueAfter ?? 500, searchSignal)
        page = await getDocumentSearchResults(client, processId, apiVersion, page.continueToken, searchSignal)
        searchResults.push(...page.results)
      }
      if (searchSignal.aborted) throw new DOMException("Aborted", "AbortError")
      clearTimeout(searchTimeout)
      signal.removeEventListener("abort", cancelSearch)

      if (signal.aborted) throw new DOMException("Aborted", "AbortError")
      updateFileRun(file, labelName, (current) => ({ ...current, status: "Rendering matches" }))
      const resultsByPage = new Map<number, Array<{ result: (typeof searchResults)[number]; index: number }>>()
      searchResults.forEach((result, index) => {
        const pageResults = resultsByPage.get(result.pageIndex) ?? []
        pageResults.push({ result, index })
        resultsByPage.set(result.pageIndex, pageResults)
      })
      const matches = new Array<TestMatch>(searchResults.length)
      for (const [pageIndex, pageResults] of resultsByPage) {
        if (signal.aborted) throw new DOMException("Aborted", "AbortError")
        const image = await loadPageImage(getPageImageUrl(client, activeSessionId, pageIndex, apiVersion), signal)
        try {
          for (const { result, index } of pageResults) {
            const imageUrl = await createMatchSnapshot(image, result, signal)
            createdUrls.push(imageUrl)
            matches[index] = {
              ...result,
              id: crypto.randomUUID(),
              imageUrl,
            }
          }
        } finally {
          image.removeAttribute("src")
        }
      }

      if (signal.aborted) throw new DOMException("Aborted", "AbortError")
      const urls = previewUrlsByCase.current.get(file.caseReference) ?? new Set<string>()
      createdUrls.forEach((url) => urls.add(url))
      previewUrlsByCase.current.set(file.caseReference, urls)
      updateFileRun(file, labelName, (current) => ({ ...current, status: "Success", results: matches }))
      return matches.length > 0
    } catch (error) {
      createdUrls.forEach((url) => URL.revokeObjectURL(url))
      updateFileRun(file, labelName, (current) => ({
        ...current,
        status: signal.aborted ? "Cancelled" : "Failed",
        error: signal.aborted ? undefined : searchTimedOut ? "Search timed out after 60 seconds." : errorMessage(error),
        statusMessage: signal.aborted ? "Run cancelled." : undefined,
      }))
      return false
    } finally {
      clearTimeout(searchTimeout)
      signal.removeEventListener("abort", cancelSearch)
    }
  }

  async function runTest(retry = false) {
    if (runController.current != null) return
    if (selectedLabel == null || selectedLabel.rawSearchTerms.length === 0) {
      setRunError(null)
      return
    }
    if (selectedLabel.rawSearchTerms.some((term) => !isTermValid(term))) {
      setRunError("Fix empty search terms or invalid regular expressions in the configuration before running a test.")
      return
    }
    const filesToRun = retry ? checkedFiles : selectedFiles
    if (filesToRun.length === 0 || loadingCases.length > 0) {
      setRunError("Select at least one file before starting a run.")
      return
    }
    if (runtime == null) {
      setRunError(runtimeError ?? "Viewer search is not ready yet.")
      return
    }

    setRunError(null)
    setSelectionNotice(null)
    setIsRunning(true)
    setStage("results")
    const labelName = selectedLabelName
    const controller = new AbortController()
    runController.current = controller
    setSelectedAttempt(null)
    setExpandedResults([])
    if (!retry) {
      clearTestResults()
      setActiveResultFileKey(fileKey(filesToRun[0]))
    }
    const patterns = createViewerSearchPatterns(selectedLabel.rawSearchTerms)
    const eligible: TestFile[] = []
    const initialRuns = new Map<string, TestFileRun>()

    for (const file of filesToRun) {
      const { previousAttempts = [], ...attempt } = getFileRun(file, labelName)
      const initialRun: TestFileRun = {
        results: [],
        previousAttempts: retry && getOutcome(attempt) != null ? [...previousAttempts, attempt] : [],
      }
      const excluded = !shouldAllowSearch(selectedLabel, file.aiClassificationGroup ?? "", file.aiClassification ?? "")
      const skipReason = getSkipReason(file)

      if (excluded) {
        initialRun.status = "Excluded"
        initialRun.statusMessage = "Excluded by Do Not Check"
      } else if (skipReason != null) {
        initialRun.status = "Skipped"
        initialRun.statusMessage = skipReason
      } else {
        eligible.push(file)
      }
      initialRuns.set(fileKey(file), initialRun)
    }
    setFilesByCase((current) =>
      Object.fromEntries(
        Object.entries(current).map(([key, files]) => [
          key,
          files.map((file) => {
            const run = initialRuns.get(fileKey(file))
            return run == null ? file : { ...file, runs: { ...file.runs, [labelName]: run } }
          }),
        ]),
      ),
    )

    let firstResultIndex = eligible.length
    let nextIndex = 0
    const apiVersion = runtime.apiVersion
    async function runNextFile() {
      while (!controller.signal.aborted && nextIndex < eligible.length) {
        const index = nextIndex++
        const hasResults = await runFile(eligible[index], patterns, controller, apiVersion, labelName)
        if (hasResults) firstResultIndex = Math.min(firstResultIndex, index)
      }
    }

    try {
      await Promise.all(Array.from({ length: Math.min(FILE_CONCURRENCY, eligible.length) }, () => runNextFile()))
      for (const file of eligible.slice(nextIndex)) {
        updateFileRun(file, labelName, (current) => ({
          ...current,
          status: "Cancelled",
          statusMessage: "Run cancelled.",
        }))
      }
    } finally {
      runController.current = null
      if (isMounted.current) {
        if (retry) setCheckedFileKeys([])
        if (!retry && firstResultIndex < eligible.length) {
          setActiveResultFileKey((current) =>
            current === fileKey(filesToRun[0]) ? fileKey(eligible[firstResultIndex]) : current,
          )
        }
        setCompletedLabels((current) => (current.includes(labelName) ? current : [...current, labelName]))
        setIsRunning(false)
      }
    }
  }

  function cancelRun() {
    runController.current?.abort()
  }

  return (
    <div className="grid min-h-0 min-w-0 flex-1 grid-cols-1 gap-4 overflow-y-auto max-lg:[overflow-wrap:anywhere] lg:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(14rem,1fr)] lg:grid-rows-[auto_minmax(0,1fr)] lg:overflow-hidden">
      <div className="min-w-0 shrink-0 space-y-3 rounded-md border bg-background p-3 sm:p-4 lg:col-start-3 lg:row-start-1">
        <div className="space-y-2">
          <label htmlFor="summarizer-test-label" className="text-sm font-medium">
            Configuration
          </label>
          <Select
            value={selectedLabelName}
            onValueChange={changeConfiguration}
            disabled={labels.length === 0 || isRunning}
          >
            <SelectTrigger
              id="summarizer-test-label"
              className="w-full min-w-0 max-sm:[&>[data-slot=select-value]]:min-w-0"
              aria-label="Configuration label"
            >
              <SelectValue placeholder="Select label" />
            </SelectTrigger>
            <SelectContent className="max-lg:max-w-(--radix-select-content-available-width) max-lg:[&_[data-slot=select-item]]:[overflow-wrap:anywhere]">
              {labels.map((label) => (
                <SelectItem key={label.label} value={label.label}>
                  {label.description === "" ? label.label : label.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs whitespace-nowrap text-muted-foreground">
            {selectedFiles.length} {selectedFiles.length === 1 ? "file" : "files"} · {selectedCases.length}{" "}
            {selectedCases.length === 1 ? "case" : "cases"}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <nav aria-label="Test stages" className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant={stage === "select" ? "secondary" : "ghost"}
              className={`rounded-full border ${SELECTED_CONTROL_CLASS}`}
              aria-pressed={stage === "select"}
              onClick={() => setStage("select")}
            >
              {stage === "select" && <CheckIcon aria-hidden="true" />}
              Select
            </Button>
            <Button
              type="button"
              size="sm"
              variant={stage === "results" ? "secondary" : "ghost"}
              className={`rounded-full border ${SELECTED_CONTROL_CLASS}`}
              aria-pressed={stage === "results"}
              onClick={() => setStage("results")}
            >
              {stage === "results" && <CheckIcon aria-hidden="true" />}
              Results
            </Button>
          </nav>
          {!isRunning ? (
            <Button
              type="button"
              size="sm"
              className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-200 dark:text-slate-950 dark:hover:bg-slate-300"
              disabled={
                runPrerequisite != null || selectedFiles.length === 0 || loadingCases.length > 0 || runtime == null
              }
              aria-describedby={runPrerequisite != null ? "summarizer-run-prerequisite" : undefined}
              onClick={() => void runTest()}
            >
              <PlayIcon /> Run Test
            </Button>
          ) : (
            <Button type="button" size="sm" variant="destructive" onClick={cancelRun}>
              <XIcon /> Cancel
            </Button>
          )}
        </div>
        {runtimeError != null && (
          <p role="alert" className="text-sm text-destructive">
            Could not connect to Viewer: {runtimeError}
          </p>
        )}
        {runError != null && (
          <p role="alert" className="text-sm text-destructive">
            {runError}
          </p>
        )}
        {(runPrerequisite != null || selectedFiles.length === 0) && (
          <div className="space-y-1 text-sm text-amber-600 dark:text-amber-200">
            {runPrerequisite != null && (
              <p id="summarizer-run-prerequisite" role="status">
                {runPrerequisite}
              </p>
            )}
            {selectedFiles.length === 0 && (
              <p>
                {selectedCases.length === 0
                  ? "Select a case to add its files before running a test."
                  : "Restore removed files or select another case before running a test."}
              </p>
            )}
          </div>
        )}
        {isRunning && (
          <p role="status" className="text-sm text-muted-foreground">
            Case and file selection is locked during a run.
          </p>
        )}
        {selectionNotice != null && (
          <p role="status" className="text-sm text-muted-foreground">
            {selectionNotice}
          </p>
        )}
      </div>

      {stage === "select" && (
        <section
          aria-labelledby="selected-cases-heading"
          className="min-h-0 min-w-0 shrink-0 rounded-md border bg-background p-3 sm:p-4 lg:col-start-3 lg:row-start-2 lg:flex lg:flex-col lg:overflow-hidden lg:rounded-none lg:border-x-0 lg:border-t-2 lg:border-b-0"
        >
          <h2 id="selected-cases-heading" className="mb-2 text-sm font-semibold">
            Selected cases ({selectedCases.length})
          </h2>
          {selectedCases.length === 0 ? (
            <p className="text-sm text-muted-foreground">No cases selected.</p>
          ) : (
            <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1 lg:flex-col lg:flex-nowrap">
              {selectedCases.map((caseReference) => {
                const caseFiles = filesByCase[caseReference] ?? []
                return (
                  <div
                    key={caseReference}
                    className="flex max-w-full min-w-0 shrink-0 flex-col items-start gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-2 text-indigo-800 max-sm:w-full lg:w-full dark:border-indigo-700 dark:bg-indigo-950 dark:text-indigo-200"
                  >
                    <div className="flex w-full min-w-0 items-center gap-2">
                      <CheckIcon aria-hidden="true" className="size-4 shrink-0" />
                      <span title={caseReference} className="min-w-0 flex-1 truncate text-sm font-medium">
                        {caseReference}
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-6 shrink-0"
                        aria-label={`Remove case ${caseReference}`}
                        disabled={isRunning || loadingCases.includes(caseReference)}
                        onClick={() => void toggleCase(caseReference)}
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="ml-6 h-7 px-2 text-xs"
                      disabled={isRunning || loadingCases.includes(caseReference) || caseFiles.length === 0}
                      onClick={() => toggleCaseFiles(caseReference)}
                    >
                      {caseFiles.some((file) => selectedFileKeySet.has(fileKey(file)))
                        ? "Deselect all files"
                        : "Select all files"}
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      <div
        className={`grid min-h-0 min-w-0 shrink-0 grid-cols-1 gap-4 lg:col-span-2 lg:col-start-1 lg:row-span-2 lg:row-start-1 lg:flex-1 lg:shrink lg:overflow-hidden ${
          stage === "select" ? "lg:grid-cols-2" : "lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]"
        }`}
      >
        {stage === "select" && (
          <section
            aria-labelledby="case-search-heading"
            className="flex min-h-0 min-w-0 flex-col gap-3 rounded-md border bg-background p-3 sm:p-4 lg:overflow-y-auto"
          >
            <div>
              <h2 id="case-search-heading" className="text-base font-semibold">
                Find cases
              </h2>
              <p className="text-sm text-muted-foreground">Search for cases, then choose the files to run.</p>
            </div>
            <form onSubmit={handleCaseSearch} className="min-w-0 shrink-0 space-y-2">
              <Input
                className="min-w-0"
                value={caseQuery}
                onChange={(event) => setCaseQuery(event.target.value)}
                placeholder="Search across fields"
                aria-label="Search across fields"
                disabled={isRunning || runtime == null}
              />
              <details className="rounded-md border p-3 text-sm">
                <summary className="cursor-pointer font-medium select-none">
                  Filters{activeFieldFilterCount > 0 ? ` (${activeFieldFilterCount} active)` : ""}
                </summary>
                <div className="mt-3 space-y-3">
                  <div className="space-y-1">
                    <label htmlFor="case-search-mode" className="text-sm font-medium">
                      Match mode
                    </label>
                    <select
                      id="case-search-mode"
                      value={searchMode}
                      onChange={(event) => setSearchMode(event.target.value === "any" ? "any" : "all")}
                      aria-label="Match mode"
                      className="h-9 w-full cursor-pointer rounded-md border bg-background px-2 text-sm disabled:cursor-not-allowed"
                      disabled={isRunning || runtime == null}
                    >
                      <option value="all">All words</option>
                      <option value="any">Any words</option>
                    </select>
                  </div>
                  <details className="text-sm">
                    <summary className="w-fit cursor-pointer text-muted-foreground select-none">Select fields</summary>
                    <fieldset
                      aria-label="Search fields"
                      className="mt-2 grid min-w-0 grid-cols-1 gap-2 rounded-md border p-2 sm:grid-cols-2"
                    >
                      <label
                        className={`flex items-center gap-2 sm:col-span-2 ${isRunning || runtime == null ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <Checkbox
                          checked={searchFields.length === searchableColumns.length}
                          disabled={isRunning || runtime == null}
                          onCheckedChange={(checked) =>
                            setSearchFields(checked === true ? searchableColumns.map((column) => column.fieldPath) : [])
                          }
                        />
                        Select all
                      </label>
                      {searchableColumns.map((column) => (
                        <label
                          key={column.fieldPath}
                          className={`flex min-w-0 items-center gap-2 ${isRunning || runtime == null ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <Checkbox
                            checked={searchFields.includes(column.fieldPath)}
                            disabled={isRunning || runtime == null}
                            onCheckedChange={() => toggleSearchField(column.fieldPath)}
                          />
                          <span className="min-w-0 max-sm:[overflow-wrap:anywhere] sm:truncate">
                            {column.displayName}
                          </span>
                        </label>
                      ))}
                    </fieldset>
                  </details>
                  {filterableTextColumns.map((column) => (
                    <div key={column.fieldPath} className="space-y-1">
                      <label htmlFor={`case-filter-${column.fieldPath}`} className="text-sm font-medium">
                        {column.displayName}
                      </label>
                      <Input
                        id={`case-filter-${column.fieldPath}`}
                        placeholder={
                          column.filterConfig?.placeholder ??
                          (column.fieldPath === "metadata/issueDates" ? "YYYY MM DD" : undefined)
                        }
                        maxLength={column.filterConfig?.maxLength}
                        value={caseFieldFilters[column.fieldPath] ?? ""}
                        onChange={(event) =>
                          setCaseFieldFilters((current) => ({ ...current, [column.fieldPath]: event.target.value }))
                        }
                        disabled={isRunning || runtime == null}
                      />
                    </div>
                  ))}
                </div>
              </details>
              <div className="flex items-start gap-2 sm:items-center">
                <Checkbox
                  id="cases-with-documents-only"
                  className="max-sm:mt-0.5"
                  checked={casesWithDocumentsOnly}
                  onCheckedChange={(checked) => {
                    const documentsOnly = checked === true
                    setCasesWithDocumentsOnly(documentsOnly)
                    submitCaseSearch(documentsOnly)
                  }}
                  disabled={isRunning || runtime == null}
                />
                <label
                  htmlFor="cases-with-documents-only"
                  className={`text-sm ${isRunning || runtime == null ? "cursor-not-allowed" : "cursor-pointer"}`}
                >
                  Cases with documents only
                </label>
              </div>
              <div className="sticky bottom-0 z-10 -mx-3 flex justify-end gap-2 border-t bg-background px-3 py-3 sm:-mx-4 sm:px-4 lg:-bottom-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isRunning || runtime == null}
                  onClick={resetCaseSearch}
                >
                  Reset
                </Button>
                <Button type="submit" disabled={isRunning || runtime == null}>
                  {caseSearchLoading ? <LoaderCircleIcon className="animate-spin" /> : <SearchIcon />}
                  Search
                </Button>
              </div>
            </form>
            {caseSearchError != null && (
              <p role="alert" className="text-sm text-destructive">
                {caseSearchError}
              </p>
            )}
            <div>
              {caseResults.length === 0 ? (
                <p className="py-4 text-sm text-muted-foreground">
                  {hasSearched ? "No cases found." : "Enter search criteria and select Search."}
                </p>
              ) : (
                <ul className="divide-y">
                  {caseResults.map((caseItem) => {
                    const selected = selectedCases.includes(caseItem.caseReference)
                    return (
                      <li key={caseItem.caseReference} className="flex items-center justify-between gap-2 py-2">
                        <span className="min-w-0 text-sm max-sm:[overflow-wrap:anywhere] sm:truncate">
                          {caseItem.caseReference}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant={selected ? "secondary" : "outline"}
                          className={`shrink-0 ${SELECTED_CONTROL_CLASS}`}
                          aria-pressed={selected}
                          disabled={isRunning || loadingCases.includes(caseItem.caseReference)}
                          onClick={() => void toggleCase(caseItem.caseReference)}
                        >
                          {selected && <CheckIcon aria-hidden="true" />}
                          {selected ? "Remove" : loadingCases.includes(caseItem.caseReference) ? "Loading" : "Select"}
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between text-sm">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={casePage <= 1 || isRunning}
                onClick={() => void searchCasePage(casePage - 1)}
              >
                Previous
              </Button>
              <span>
                {casePage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={casePage >= totalPages || isRunning}
                onClick={() => void searchCasePage(casePage + 1)}
              >
                Next
              </Button>
            </div>
          </section>
        )}

        <section
          aria-labelledby="files-heading"
          className={`flex min-h-0 min-w-0 flex-col gap-3 rounded-md border bg-background p-3 sm:p-4 lg:overflow-y-auto ${
            stage === "results" ? "max-h-[45dvh] overflow-y-auto lg:max-h-none" : ""
          }`}
        >
          <details className="shrink-0 rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium select-none">
              Classifications
              {resultClassificationFilters.length > 0 ? ` (${resultClassificationFilters.length} selected)` : ""}
            </summary>
            {classifications.length === 0 ? (
              <p className="my-2 text-sm text-muted-foreground">No files selected.</p>
            ) : (
              <div className="mt-2 flex max-h-36 flex-wrap gap-1 overflow-y-auto">
                {classifications.map((classification) => {
                  return (
                    <Button
                      key={classification}
                      type="button"
                      size="sm"
                      variant={resultClassificationFilters.includes(classification) ? "secondary" : "ghost"}
                      className={`max-w-full max-lg:h-auto max-lg:[overflow-wrap:anywhere] max-lg:whitespace-normal ${SELECTED_CONTROL_CLASS}`}
                      aria-pressed={resultClassificationFilters.includes(classification)}
                      onClick={() =>
                        setResultClassificationFilters((current) =>
                          current.includes(classification)
                            ? current.filter((value) => value !== classification)
                            : [...current, classification],
                        )
                      }
                    >
                      {resultClassificationFilters.includes(classification) && <CheckIcon aria-hidden="true" />}
                      {classification}
                    </Button>
                  )
                })}
              </div>
            )}
          </details>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <h2 id="files-heading" className="text-base font-semibold">
              Files ({selectedFiles.length})
            </h2>
            <div className="flex min-w-0 flex-wrap items-center gap-2 max-sm:w-full">
              {stage === "results" && (
                <Select
                  value={fileFilter}
                  onValueChange={(value) => {
                    const filter = FILE_FILTERS.find((filter) => filter === value)
                    if (filter != null) setFileFilter(filter)
                  }}
                >
                  <SelectTrigger className="w-44 max-w-full max-sm:min-w-0 max-sm:flex-1" aria-label="Filter files">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILE_FILTERS.map((filter) => (
                      <SelectItem key={filter} value={filter}>
                        {filter} ({filesByOutcome[filter].length})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={fileFilter === "All" && resultClassificationFilters.length === 0}
                onClick={() => {
                  setFileFilter("All")
                  setResultClassificationFilters([])
                }}
              >
                Clear
              </Button>
            </div>
          </div>
          {stage === "select" ? (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm max-sm:flex-col max-sm:items-stretch">
              <span>
                {removedFiles.length} {removedFiles.length === 1 ? "file" : "files"} removed
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isRunning || removedFiles.length === 0}
                onClick={() => setSelectedFileKeys(allFiles.map(fileKey))}
              >
                Restore removed files
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm max-sm:flex-col max-sm:items-stretch">
              <label className={`flex items-center gap-2 ${isRunning ? "cursor-not-allowed" : "cursor-pointer"}`}>
                <Checkbox
                  checked={
                    allVisibleChecked
                      ? true
                      : checkableFiles.some((file) => checkedFileKeySet.has(fileKey(file)))
                        ? "indeterminate"
                        : false
                  }
                  disabled={isRunning || checkableFiles.length === 0}
                  onCheckedChange={(checked) => {
                    const keys = new Set(checkableFiles.map(fileKey))
                    setCheckedFileKeys((current) =>
                      checked === true ? [...new Set([...current, ...keys])] : current.filter((key) => !keys.has(key)),
                    )
                  }}
                />
                Select all in filter
              </label>
              <Button
                type="button"
                size="sm"
                disabled={isRunning || checkedFiles.length === 0 || runPrerequisite != null}
                onClick={() => void runTest(true)}
              >
                Re-run selected ({checkedFiles.length})
              </Button>
            </div>
          )}
          <div>
            {selectedCases.map((caseReference) => (
              <div key={caseReference}>
                {loadingCases.includes(caseReference) && (
                  <p className="border-b py-2 text-xs text-muted-foreground">Loading {caseReference} files...</p>
                )}
                {caseFileErrors[caseReference] != null && (
                  <p role="alert" className="border-b py-2 text-xs text-destructive">
                    {caseReference}: {caseFileErrors[caseReference]}
                  </p>
                )}
              </div>
            ))}
            {visibleFiles.map((file) => {
              const run = getFileRun(file, selectedLabelName)
              const active = stage === "results" && activeResultFileKey === fileKey(file)
              return (
                <div
                  key={fileKey(file)}
                  className={`flex flex-col border-b border-l-4 px-2 py-2 text-sm ${
                    active
                      ? "rounded-md border-indigo-200 border-l-indigo-400 bg-indigo-50 text-slate-900 dark:border-indigo-700 dark:border-l-indigo-400 dark:bg-indigo-950 dark:text-indigo-100"
                      : "border-l-transparent"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {stage === "results" && (
                      <Checkbox
                        aria-label={`Re-run ${file.displayName}`}
                        checked={checkedFileKeySet.has(fileKey(file))}
                        disabled={isRunning || getOutcome(run) == null}
                        onCheckedChange={(checked) =>
                          setCheckedFileKeys((current) =>
                            checked === true
                              ? [...new Set([...current, fileKey(file)])]
                              : current.filter((key) => key !== fileKey(file)),
                          )
                        }
                      />
                    )}
                    <button
                      type="button"
                      className={`min-w-0 flex-1 cursor-pointer text-left disabled:cursor-not-allowed max-lg:[overflow-wrap:anywhere] lg:truncate ${
                        active ? "font-semibold" : ""
                      }`}
                      disabled={stage === "select"}
                      aria-label={`[${file.aiClassificationGroup == null || file.aiClassificationGroup === "" ? "Unknown" : file.aiClassificationGroup}] ${file.displayName}`}
                      aria-pressed={active}
                      onClick={() => selectResultFile(fileKey(file))}
                    >
                      {active && <CheckIcon aria-hidden="true" className="mr-1 inline size-4" />}
                      <span
                        className={`mr-2 inline-block rounded px-1.5 py-0.5 text-xs font-normal max-lg:max-w-full ${
                          file.aiClassificationGroup == null ||
                          file.aiClassificationGroup === "" ||
                          file.aiClassificationGroup === "Unknown"
                            ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                            : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200"
                        }`}
                      >
                        {file.aiClassificationGroup == null || file.aiClassificationGroup === ""
                          ? "Unknown"
                          : file.aiClassificationGroup}
                      </span>
                      {file.displayName}
                    </button>
                    <a
                      href={`${config.viewerAppUrl.replace(/\/$/, "")}/${encodeURIComponent(brand)}/${encodeURIComponent(file.caseReference)}/${encodeURIComponent(file.fileReference)}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Open File"
                      aria-label={`Open ${file.displayName} in Viewer`}
                      className="shrink-0 cursor-pointer"
                    >
                      <ExternalLinkIcon className="size-3" />
                    </a>
                    {stage === "select" && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-label={`Remove file ${file.displayName}`}
                        disabled={isRunning}
                        onClick={() => removeFile(file)}
                      >
                        <XIcon className="size-4" />
                      </Button>
                    )}
                  </div>
                  {run.status != null && (
                    <p
                      role="status"
                      aria-atomic="true"
                      className={`mt-1 flex w-fit items-center gap-1.5 rounded px-2 py-1 text-xs font-medium ${
                        inProgress(run.status)
                          ? "bg-blue-100 text-blue-900 ring-1 ring-blue-300 dark:bg-blue-900 dark:text-blue-100 dark:ring-blue-500"
                          : run.status === "Success"
                            ? "bg-green-50 text-green-800 dark:bg-green-950 dark:text-green-200"
                            : run.status === "Failed"
                              ? "bg-red-50 text-red-800 dark:bg-red-950 dark:text-red-200"
                              : run.status === "Skipped" || run.status === "Excluded"
                                ? "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
                                : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                      }`}
                    >
                      {inProgress(run.status) && (
                        <LoaderCircleIcon
                          aria-hidden="true"
                          className="size-3.5 animate-spin motion-reduce:animate-none"
                        />
                      )}
                      {getOutcome(run) ?? run.status} · {run.results?.length ?? 0} results
                    </p>
                  )}
                  {run.previousAttempts != null && run.previousAttempts.length > 0 && (
                    <p className="mt-1 text-xs font-medium">
                      {run.previousAttempts.length + 1} attempts{isFlaky(run) ? " · Flaky" : ""}
                    </p>
                  )}
                  {run.statusMessage != null && <p className="text-xs text-muted-foreground">{run.statusMessage}</p>}
                  {run.status === "Failed" && run.error != null && (
                    <details className="mt-1 text-xs">
                      <summary className="cursor-pointer">Error details</summary>
                      <pre className="overflow-auto whitespace-pre-wrap text-destructive max-lg:[overflow-wrap:anywhere]">
                        {run.error}
                      </pre>
                    </details>
                  )}
                </div>
              )
            })}
            {selectedCases.length > 0 && allSelectedCasesLoaded && allFiles.length === 0 && (
              <p className="py-4 text-sm text-muted-foreground">No files found for selected cases.</p>
            )}
            {(selectedCases.length === 0 || (selectedFiles.length === 0 && allFiles.length > 0)) && (
              <p className="py-4 text-sm text-muted-foreground">No files selected.</p>
            )}
            {selectedFiles.length > 0 && visibleFiles.length === 0 && (
              <p className="py-4 text-sm text-muted-foreground">No files match this filter.</p>
            )}
          </div>
        </section>

        {stage === "results" && (
          <section
            aria-labelledby="results-heading"
            className="flex min-h-0 min-w-0 flex-col gap-3 rounded-md border bg-background p-3 sm:p-4 lg:overflow-y-auto"
          >
            <div>
              <h2 id="results-heading" className="text-base font-semibold">
                Result previews ({visibleResults.length})
              </h2>
              {activeFile != null && <p className="mt-1 text-sm font-medium break-words">{activeFile.displayName}</p>}
              <p className="text-sm text-muted-foreground">
                Review matches below. Open a preview to see it at full size.
              </p>
            </div>
            {attempts.length > 1 && (
              <div className="space-y-1">
                <label htmlFor="summarizer-result-attempt" className="text-sm font-medium">
                  Attempt
                </label>
                <select
                  id="summarizer-result-attempt"
                  className="h-9 w-full cursor-pointer rounded-md border bg-background px-2 text-sm"
                  value={selectedAttempt ?? attempts.length - 1}
                  onChange={(event) => {
                    const index = Number(event.target.value)
                    setSelectedAttempt(index === attempts.length - 1 ? null : index)
                    setExpandedResults([])
                  }}
                >
                  {attempts.map((attempt, index) => (
                    <option key={index} value={index}>
                      Attempt {index + 1} - {getOutcome(attempt) ?? attempt.status ?? "Queued"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {activeAttempt?.status != null && (
              <p role="status" className="text-sm">
                {getOutcome(activeAttempt) ?? activeAttempt.status}
                {activeAttempt.statusMessage != null ? `: ${activeAttempt.statusMessage}` : ""}
              </p>
            )}
            {activeAttempt?.error != null && (
              <div role="alert" className="rounded-md border border-destructive p-3 text-sm text-destructive">
                <p className="font-medium">Error details</p>
                <pre className="mt-1 break-words whitespace-pre-wrap max-lg:[overflow-wrap:anywhere]">
                  {activeAttempt.error}
                </pre>
              </div>
            )}
            {!isRunning && !isRunComplete && visibleResults.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Choose files in Select, then run a test to review matches here.
              </p>
            )}
            <MatchPreviews
              matches={visibleResults}
              brand={brand}
              expandedResults={expandedResults}
              setExpandedResults={setExpandedResults}
            />
            {activeAttempt?.status === "Success" && visibleResults.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">No matches found for this file.</p>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

const MatchPreviews = memo(function MatchPreviews({
  matches,
  brand,
  expandedResults,
  setExpandedResults,
}: {
  matches: Array<{ file: TestFile; result: TestMatch }>
  brand: string
  expandedResults: string[]
  setExpandedResults: Dispatch<SetStateAction<string[]>>
}) {
  const expandedResultSet = new Set(expandedResults)
  return (
    <div className="grid items-start gap-3 xl:grid-cols-2">
      {matches.map(({ file, result }) => {
        const resultKey = `${fileKey(file)}:${result.id}`
        const showContext = expandedResultSet.has(resultKey)

        return (
          <article
            key={resultKey}
            className="min-w-0 overflow-hidden rounded-md border"
            style={{ contentVisibility: "auto", containIntrinsicSize: "auto 300px" }}
          >
            <header className="space-y-2 bg-muted px-3 py-2 text-sm">
              <p className="font-medium break-words">{file.displayName}</p>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-expanded={showContext}
                  onClick={() =>
                    setExpandedResults((current) =>
                      current.includes(resultKey)
                        ? current.filter((key) => key !== resultKey)
                        : [...current, resultKey],
                    )
                  }
                >
                  {showContext ? "Hide context" : "Show context"}
                </Button>
                <a
                  href={`${config.viewerAppUrl.replace(/\/$/, "")}/${encodeURIComponent(brand)}/${encodeURIComponent(file.caseReference)}/${encodeURIComponent(file.fileReference)}/page-${result.pageIndex + 1}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex cursor-pointer items-center gap-1 underline"
                >
                  Page {result.pageIndex + 1} <ExternalLinkIcon className="size-3" />
                </a>
              </div>
            </header>
            {showContext && (
              <div className="space-y-2 px-3 pt-3 text-sm">
                <p>
                  <span className="font-medium">Search term:</span> {result.text}
                </p>
                <p className="whitespace-pre-wrap">
                  <span className="font-medium">Context:</span> {result.context}
                </p>
              </div>
            )}
            {result.imageUrl != null && (
              <Dialog>
                <DialogTrigger asChild>
                  <button
                    type="button"
                    className="block w-full cursor-zoom-in rounded-sm p-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    aria-label={`Enlarge match on page ${result.pageIndex + 1} of ${file.displayName}`}
                  >
                    <img
                      src={result.imageUrl}
                      alt={`Match on page ${result.pageIndex + 1} of ${file.displayName}`}
                      loading="lazy"
                      decoding="async"
                      className="max-h-64 w-full object-contain"
                    />
                  </button>
                </DialogTrigger>
                <DialogContent className="max-h-[90dvh] max-lg:overflow-y-auto sm:max-w-5xl">
                  <DialogTitle className="pr-6 break-words max-lg:[overflow-wrap:anywhere]">
                    {file.displayName}
                  </DialogTitle>
                  <DialogDescription>Match on page {result.pageIndex + 1}</DialogDescription>
                  <div className="min-h-0 overflow-auto">
                    <img
                      src={result.imageUrl}
                      alt={`Enlarged match on page ${result.pageIndex + 1} of ${file.displayName}`}
                      className="w-full"
                    />
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </article>
        )
      })}
    </div>
  )
})
